import { IMAGE_CONFIG } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

import { AuthService } from './core/auth/auth.service';
import { authTokenInterceptor } from './core/auth/auth.interceptor';
import { httpErrorToastInterceptor } from './core/auth/http-error-toast.interceptor';
import { APP_ENV, AppEnvironment } from './core/config/app-environment';
import { HospitalityApiService } from './core/services/hospitality-api.service';
import { HospitalityStoreService } from './core/services/hospitality-store.service';
import { HttpHospitalityApiService } from './core/services/http-hospitality-api.service';
import { MockHospitalityApiService } from './core/services/mock-hospitality-api.service';
import { routes } from './app.routes';

export function buildAppConfig(environment: AppEnvironment): ApplicationConfig {
  return {
    providers: [
      provideBrowserGlobalErrorListeners(),
      {
        provide: IMAGE_CONFIG,
        useValue: {
          disableImageSizeWarning: true
        }
      },
      { provide: APP_ENV, useValue: environment },
      provideRouter(
        routes,
        withInMemoryScrolling({
          scrollPositionRestoration: 'enabled'
        })
      ),
      provideHttpClient(withInterceptors([authTokenInterceptor, httpErrorToastInterceptor])),
      {
        provide: HospitalityApiService,
        useFactory: (
          appEnvironment: AppEnvironment,
          mockApi: MockHospitalityApiService,
          httpApi: HttpHospitalityApiService
        ) => (appEnvironment.auth.mode === 'mock' ? mockApi : httpApi),
        deps: [APP_ENV, MockHospitalityApiService, HttpHospitalityApiService]
      },
      provideAppInitializer(async () => {
        const auth = inject(AuthService);
        const store = inject(HospitalityStoreService);

        await auth.init();

        if (auth.authMode() === 'mock' || auth.authenticated()) {
          await store.load();
          return;
        }

        store.clearForLoggedOutState();
      })
    ]
  };
}
